import React, { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { TriangleAlert } from 'lucide-react';

import useTMSContext from 'hooks/useTMSContext';
import { Exception } from 'types/Exception';
import { TMS, TMSTimezoneConfig } from 'types/enums/Integrations';

import { Flex } from './layout/Flex';
import { Typography } from './typography';

type ExceptionProps = Exception;

type ExceptionTimelineProps = {
  events: ExceptionProps[];
};

type ExpandableNoteProps = {
  note: string;
};

const ExpandableNote: React.FC<ExpandableNoteProps> = ({ note }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el || isExpanded) return;

    const checkOverflow = () => {
      // scrollHeight is the full content height
      // clientHeight is the visible (clamped) height
      // We add a tiny buffer for sub-pixel differences
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      setCanExpand(hasOverflow);
    };

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    checkOverflow(); // Initial check

    return () => observer.disconnect();
  }, [note, isExpanded]);

  // Split by newline and filter out empty segments
  const segments = note
    ? note.split('\n').filter((s) => s.trim().length > 0)
    : [];

  return (
    <Flex direction='col' className='w-full'>
      <div
        ref={textRef}
        className={`text-neutral-500 text-xs leading-5 transition-all duration-200 ${
          isExpanded
            ? 'block'
            : 'line-clamp-3 overflow-hidden whitespace-pre-line'
        }`}
      >
        {isExpanded ? (
          segments.map((segment, idx) => (
            <Typography
              key={idx}
              variant='body-xs'
              textColor='muted'
              className={idx > 0 ? 'mt-3 pt-2 border-t border-neutral-100' : ''}
            >
              {segment}
            </Typography>
          ))
        ) : (
          <Typography variant='body-xs' textColor='muted'>
            {note}
          </Typography>
        )}
      </div>
      {(canExpand || isExpanded) && (
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className='text-info-main text-xs leading-5 hover:underline font-semibold focus:outline-none w-fit mt-2 bg-transparent border-none p-0 cursor-pointer text-left shrink-0'
        >
          {isExpanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </Flex>
  );
};

const ExceptionTimeline: React.FC<ExceptionTimelineProps> = ({ events }) => {
  if (!events) {
    return null;
  }
  const { tmsName } = useTMSContext();
  const isTZAgnostic =
    TMSTimezoneConfig[tmsName as TMS].isTrackAndTraceTimezoneAgnostic;

  const shortTZ = dayjs().tz().format('z');
  const normalizedTZ = isTZAgnostic ? '' : ` ${shortTZ}`;

  return (
    <>
      {isTZAgnostic && (
        <span className='text-xs font-normal italic text-neutral-500 ms-4'>
          {`Note: Unless otherwise stated, all timestamps are in the timezone of the check call's location.`}
        </span>
      )}

      <ol className='relative border-s ms-4 border-info-main px-2'>
        {events
          .sort((a, b) =>
            // Sort in descending order (most recent first)

            isTZAgnostic
              ? new Date(b.dateTimeWithoutTimezone).getTime() -
                new Date(a.dateTimeWithoutTimezone).getTime()
              : new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
          )
          .map((event) => (
            <li className='mb-10 ms-3' key={null}>
              <span className='absolute flex items-center justify-center w-6 h-6 mr-2 bg-neutral-50 rounded-full -start-3 ring-1 ring-offset-4 ring-offset-neutral-50 ring-info-main'>
                <TriangleAlert color='#E92C2C' size={'sm'} strokeWidth={1} />
              </span>
              <div className='flex items-start text-neutral-600 text-sm leading-6 mt-6 justify-between'>
                <span>{event.eventCode || event.status}</span>
                <span>
                  <time className='flex text-right text-xs font-normal leading-none text-neutral-500 pl-6'>
                    {(() => {
                      // Handle if somehow no timestamp provided
                      if (!event.dateTime && !event.dateTimeWithoutTimezone) {
                        return <Typography>N/A</Typography>;
                      }

                      const dateTime = isTZAgnostic
                        ? new Date(event.dateTimeWithoutTimezone)
                        : new Date(event.dateTime);

                      return (
                        <>
                          {dayjs(dateTime)
                            .tz(isTZAgnostic ? 'UTC' : undefined) // If valid TZ, undefined = display in user's locale
                            .format('M/D/YY')}
                          <br />
                          {`${dayjs(dateTime)
                            .tz(isTZAgnostic ? 'UTC' : undefined) // If valid TZ, undefined = display in user's locale
                            .format('H:mm')} ${normalizedTZ}`}
                        </>
                      );
                    })()}
                  </time>
                </span>
              </div>

              {/* For Relay loads, highlight the fact that these are just check calls where Issue = true */}
              {tmsName === TMS.Relay && (
                <div>
                  <span
                    className={`text-xs leading-5 inline bg-error-50 text-error-main displa`}
                  >
                    Issue:{' '}
                  </span>
                  <span
                    className={`text-xs leading-5 inline text-error-main bg-error-50 px-2`}
                  >
                    {`${true}`}
                  </span>
                </div>
              )}

              {event.status && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Status:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline capitalize'>
                    {event.status}
                  </span>
                </div>
              )}

              {event.isOnTime !== null && (
                <div>
                  <span
                    className={`text-neutral-400 text-xs leading-5 inline ${!event.isOnTime ? 'text-error-main bg-error-50' : ''}`}
                  >
                    On Time:{' '}
                  </span>
                  <span
                    className={`text-neutral-500 text-xs leading-5 inline ${!event.isOnTime ? 'text-error-main bg-error-50 px-2' : ''}`}
                  >
                    {`${event.isOnTime}`}
                  </span>
                </div>
              )}

              {event.carrier && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Carrier:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.carrier}
                  </span>
                </div>
              )}

              {event.driver && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Driver:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.driver}
                  </span>
                </div>
              )}

              {event.trailer && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Trailer:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.trailer}
                  </span>
                </div>
              )}

              {event.whoEntered && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Author:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.whoEntered}
                  </span>
                </div>
              )}

              {event.owner && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Owner:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.owner}
                  </span>
                </div>
              )}

              {event.source && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Source:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.source}
                  </span>
                </div>
              )}

              {event.note && (
                <div className='flex items-start'>
                  <span className='text-neutral-400 text-xs leading-5 shrink-0'>
                    Notes:{' '}
                  </span>
                  <div className='flex-grow'>
                    <ExpandableNote note={event.note} />
                  </div>
                </div>
              )}

              {event.fault && (
                <div>
                  <span className='text-neutral-400 text-xs leading-5 inline'>
                    Fault:{' '}
                  </span>
                  <span className='text-neutral-500 text-xs leading-5 inline'>
                    {event.fault}
                  </span>
                </div>
              )}
            </li>
          ))}
      </ol>
    </>
  );
};

export { ExceptionTimeline, ExceptionProps, ExceptionTimelineProps };
